import { Link } from "react-router-dom";
import { PRODUCTS, IMAGES } from "@/lib/site";
import { Section, Overline, FadeIn, PageHero } from "@/components/Primitives";
import { ArrowUpRight, Check } from "lucide-react";

export default function Products() {
  return (
    <main data-testid="products-page">
      <PageHero
        overline="Commodities Portfolio"
        title={
          <>
            Pulses, Oilseeds &
            <br />
            <em className="not-italic text-[#2A4D3B]">Cash Crops at scale.</em>
          </>
        }
        subtitle="A complete sourcing portfolio across the world's major origins. Every product is specced, inspected and shipped to international food and feed standards."
      />

      {PRODUCTS.map((p, idx) => (
        <Section
          key={p.slug}
          testid={`product-section-${p.slug}`}
          className={idx % 2 === 0 ? "bg-[#FDFBF7]" : "bg-[#F6F4F0]"}
        >
          <div className={`grid lg:grid-cols-12 gap-12 lg:gap-20 items-center ${idx % 2 === 1 ? "lg:[&>*:first-child]:order-2" : ""}`}>
            <FadeIn className="lg:col-span-6">
              <div className="space-y-5">
                {/* If product has a pair image, show two photos side-by-side as the primary visual */}
                {p.pairImage ? (
                  <div className="grid grid-cols-2 gap-3">
                    <div className="relative aspect-square rounded-2xl overflow-hidden border-4 border-[#FDFBF7] shadow-xl">
                      <img
                        src={p.image}
                        alt={`${p.name} variety 1`}
                        className="h-full w-full object-cover"
                      />
                    </div>
                    <div className="relative aspect-square rounded-2xl overflow-hidden border-4 border-[#FDFBF7] shadow-xl">
                      <img
                        src={p.pairImage}
                        alt={`${p.name} variety 2`}
                        className="h-full w-full object-cover"
                      />
                    </div>
                  </div>
                ) : (
                  <div className="relative aspect-[4/3] rounded-3xl overflow-hidden border-4 border-[#FDFBF7] shadow-2xl">
                    <img
                      src={p.image}
                      alt={p.name}
                      className="h-full w-full object-cover"
                    />
                  </div>
                )}
                {p.secondaryImage && (
                  <div className="relative aspect-[4/3] rounded-3xl overflow-hidden border-4 border-[#FDFBF7] shadow-2xl">
                    <img
                      src={p.secondaryImage}
                      alt={`${p.name} secondary`}
                      className="h-full w-full object-cover"
                    />
                  </div>
                )}
                {p.tertiaryImage && (
                  <div className="relative aspect-[4/3] rounded-3xl overflow-hidden border-4 border-[#FDFBF7] shadow-2xl">
                    <img
                      src={p.tertiaryImage}
                      alt={`${p.name} tertiary`}
                      className="h-full w-full object-cover"
                    />
                  </div>
                )}
              </div>
            </FadeIn>
            <div className="lg:col-span-6">
              <Overline>Category</Overline>
              <h2 className="mt-5 font-serif-display text-4xl sm:text-5xl tracking-tight leading-[1.05] text-[#1A1A1A]">
                {p.name}
              </h2>
              <p className="mt-6 text-base sm:text-lg text-[#4A4540] leading-relaxed">{p.summary}</p>

              <ul className="mt-8 grid sm:grid-cols-2 gap-x-6 gap-y-3">
                {p.items.map((item) => (
                  <li
                    key={item}
                    data-testid={`product-item-${p.slug}-${item.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                    className="flex items-start gap-3 text-sm text-[#1A1A1A]"
                  >
                    <Check className="h-4 w-4 mt-1 text-[#2A4D3B] shrink-0" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>

              <Link
                to="/contact"
                data-testid={`product-cta-${p.slug}`}
                className="mt-10 inline-flex items-center gap-2 rounded-full bg-[#2A4D3B] hover:bg-[#3A634E] text-[#FDFBF7] px-6 py-3.5 text-sm font-semibold transition-colors"
              >
                Enquire about {p.name} <ArrowUpRight className="h-4 w-4" />
              </Link>
            </div>
          </div>
        </Section>
      ))}

      {/* PULSE GALLERY removed per request */}

      <Section testid="products-cta-section" className="bg-[#1F3A2D] text-[#F6F4F0]">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <Overline dark>Need something else?</Overline>
            <h2 className="mt-5 font-serif-display text-4xl sm:text-5xl leading-[1.05]">
              Have a specific origin, grade or spec in mind?
            </h2>
          </div>
          <div className="lg:justify-self-end">
            <p className="text-[#C8C2B5] max-w-md mb-8">
              Our trading desk handles bespoke sourcing requests. Send us your specification and we'll
              come back with options across origins.
            </p>
            <Link
              to="/contact"
              data-testid="products-bespoke-cta"
              className="inline-flex items-center gap-2 rounded-full bg-[#FDFBF7] text-[#1A1A1A] px-7 py-4 text-sm font-semibold hover:bg-[#D8A48F] transition-colors"
            >
              Send your spec →
            </Link>
          </div>
        </div>
      </Section>
    </main>
  );
}
